import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { UserPlus, Mail, Lock, AlertTriangle, Loader2, Eye, EyeOff, User, Phone, MapPin } from 'lucide-react';
import { supabase } from '../../lib/supabase';

export default function Register() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    fullName: '',
    phone: '',
    shareLocation: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      setSuccess('Hesabınız oluşturuluyor...');

      // Kullanıcı oluştur
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            full_name: formData.fullName,
            phone: formData.phone,
            share_location: formData.shareLocation
          }
        }
      });

      if (authError) {
        if (authError.message.includes('already registered')) {
          throw new Error('Bu e-posta adresi zaten kayıtlı');
        }
        throw authError;
      }

      if (!authData.user?.id) {
        throw new Error('Kullanıcı oluşturulamadı');
      }

      setSuccess('Hesabınız başarıyla oluşturuldu!');

      // Konum bilgisini kaydet
      if (formData.shareLocation) {
        setSuccess('Konum bilgileriniz alınıyor...');
        try {
          const position = await new Promise<GeolocationPosition>((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              enableHighAccuracy: true,
              timeout: 5000,
              maximumAge: 0
            });
          });

          await supabase.rpc('update_user_location', {
            user_id: authData.user.id,
            lat: position.coords.latitude,
            long: position.coords.longitude
          });
        } catch (locationError) {
          console.error('Konum alınamadı:', locationError);
        }
      }

      setSuccess('Yönlendiriliyorsunuz...');
      setTimeout(() => {
        navigate('/');
      }, 1000);
    } catch (err) {
      console.error('Registration error:', err);
      setError(err instanceof Error ? err.message : 'Kayıt olurken bir hata oluştu');
      setSuccess(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1a1d24] flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-[#24272e] rounded-2xl p-8 shadow-xl border border-white/5">
          <div className="flex items-center gap-3 mb-8">
            <div className="p-3 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-xl shadow-lg shadow-yellow-400/20">
              <UserPlus className="w-6 h-6 text-gray-900" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Kayıt Ol</h1>
              <p className="text-gray-400 text-sm">Yeni bir hesap oluşturun</p>
            </div>
          </div>

          <form onSubmit={handleRegister} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">
                Ad Soyad
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  className="w-full bg-[#1a1d24] text-white px-4 py-3 rounded-xl border border-white/10 focus:border-yellow-400/50 focus:ring-2 focus:ring-yellow-400/20 focus:outline-none placeholder-gray-500 pl-11"
                  placeholder="Adınız Soyadınız"
                  required
                  disabled={loading}
                />
                <User className="w-5 h-5 text-gray-400 absolute left-4 top-3.5" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">
                E-posta Adresi
              </label>
              <div className="relative">
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full bg-[#1a1d24] text-white px-4 py-3 rounded-xl border border-white/10 focus:border-yellow-400/50 focus:ring-2 focus:ring-yellow-400/20 focus:outline-none placeholder-gray-500 pl-11"
                  placeholder="ornek@mail.com"
                  required
                  disabled={loading}
                />
                <Mail className="w-5 h-5 text-gray-400 absolute left-4 top-3.5" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">
                Telefon Numarası
              </label>
              <div className="relative">
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full bg-[#1a1d24] text-white px-4 py-3 rounded-xl border border-white/10 focus:border-yellow-400/50 focus:ring-2 focus:ring-yellow-400/20 focus:outline-none placeholder-gray-500 pl-11"
                  placeholder="(5XX) XXX XX XX"
                  required
                  disabled={loading}
                />
                <Phone className="w-5 h-5 text-gray-400 absolute left-4 top-3.5" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">
                Şifre
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full bg-[#1a1d24] text-white px-4 py-3 rounded-xl border border-white/10 focus:border-yellow-400/50 focus:ring-2 focus:ring-yellow-400/20 focus:outline-none placeholder-gray-500 pl-11 pr-11"
                  placeholder="••••••••"
                  required
                  minLength={6}
                  disabled={loading}
                />
                <Lock className="w-5 h-5 text-gray-400 absolute left-4 top-3.5" />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-3.5 text-gray-400 hover:text-gray-300"
                  disabled={loading}
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
              <p className="mt-1 text-sm text-gray-400">
                En az 6 karakter uzunluğunda olmalıdır
              </p>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="shareLocation"
                checked={formData.shareLocation}
                onChange={(e) => setFormData({ ...formData, shareLocation: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 text-yellow-400 focus:ring-yellow-400/20"
                disabled={loading}
              />
              <label htmlFor="shareLocation" className="text-sm text-gray-300 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-gray-400" />
                Konumumu paylaş
              </label>
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            {success && (
              <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4 flex items-center gap-3">
                <Loader2 className="w-5 h-5 text-green-400 animate-spin" />
                <p className="text-green-400 text-sm">{success}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-gray-900 font-semibold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Kayıt Yapılıyor...</span>
                </>
              ) : (
                <span>Kayıt Ol</span>
              )}
            </button>

            <div className="text-center">
              <p className="text-gray-400">
                Zaten hesabınız var mı?{' '}
                <Link to="/giris" className="text-yellow-400 hover:text-yellow-500">
                  Giriş Yap
                </Link>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}